import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { IconRegularChevronLeft, IconRegularChevronRight } from '@notino/react-styleguide'

// hooks
import useMessages from '../../../hooks/useMessages'
import useIsMobile from '../../../hooks/useIsMobile'
import useScrollableTabList from '../../../hooks/useScrollableTabList'

// components
import ReservationBookingModalLayout from '../ReservationBookingModalLayout/ReservationBookingModalLayout'
import SalonServiceDetail from '../../SalonServiceDetail/SalonServiceDetail'
import SalonServicesCards from '../../../pages/SalonPage/components/SalonServicesCards/SalonServicesCards'

// types
import { SalonServicesResponse, ServiceCardCallbackData, ServiceDetailData, ServicesTopCategoryData } from '../../../types/types'
import { SelectedIndustryData } from '../reservationBookingModalHelpers'

// utils
import { getValidServiceCategories } from '../../../utils/helper'
import { getModalCategoryClickedEvent } from '../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../utils/dataLayer'

// styles
import { BREAKPOINTS_INTS } from '../../../styles/constants'
import * as SC from './Step0ServiceSelectionStyles'

type Props = PropsWithChildren<{
	selectedIndustryData: SelectedIndustryData
	fallbackHeaderImage: string | undefined
	salonServicesData: SalonServicesResponse
	onClose: () => void
	onBackButtonClick?: () => void
	onServiceCardButtonClick: (data: ServiceCardCallbackData) => Promise<void>
	phoneNumber: string
}>

type IndustriesTabsProps = {
	isMobileView: boolean
	selectedTabKey: string
	categoriesData: SalonServicesResponse['groupedServicesByCategory']
	onTabsChange: (industry: ServicesTopCategoryData) => void
}

const getTabButtonKey = (isMobileView: boolean, industryID: string) => {
	return isMobileView ? `mobile-tab-${industryID}` : `desktop-tab-${industryID}`
}

const getIndustryPanelId = (industryID: string) => `industry-panel-${industryID}`

const IndustriesTabs = (props: IndustriesTabsProps) => {
	const { isMobileView, selectedTabKey, categoriesData, onTabsChange } = props
	const categories = getValidServiceCategories(categoriesData)
	const prefixedSelectedTabKey = getTabButtonKey(isMobileView, selectedTabKey)

	const { scrollElementRef, isOverflown, isScrolledLeft, isScrolledRight, scrollLeft, scrollRight, handleTabKeyDown, handleTabFocus } = useScrollableTabList<
		ServicesTopCategoryData,
		HTMLDivElement
	>({
		selectedId: selectedTabKey,
		items: categories,
		getTabButtonId: (id) => getTabButtonKey(isMobileView, id),
		onSelect: onTabsChange
	})

	return (
		<SC.TabsContainer>
			{isScrolledLeft && (
				<SC.ScrollLeftButton $isMobileView={isMobileView} type='button' tabIndex={-1} aria-hidden={true} onClick={scrollLeft} style={{ zIndex: 10 }}>
					<IconRegularChevronLeft color={'icon.inverse'} />
				</SC.ScrollLeftButton>
			)}
			<SC.Tabs role={'tablist'} ref={scrollElementRef} $isScrollLeft={isOverflown && isScrolledLeft} $isScrollRight={isOverflown && isScrolledRight}>
				{categoriesData.map((industry) => {
					if (!industry.category) {
						return null
					}
					const { category } = industry
					const key = getTabButtonKey(isMobileView, category.id)
					const isActive = prefixedSelectedTabKey === key
					const panelId = getIndustryPanelId(category.id)

					return (
						<SC.TabButton
							key={key}
							id={key}
							type='button'
							$isMobileView={isMobileView}
							tabIndex={isActive ? 0 : -1}
							role={'tab'}
							aria-selected={isActive}
							aria-controls={panelId}
							onClick={() => onTabsChange(category)}
							onKeyDown={(event) => handleTabKeyDown(event, category)}
							onFocus={handleTabFocus}
						>
							{category.name}
						</SC.TabButton>
					)
				})}
			</SC.Tabs>
			{isScrolledRight && (
				<SC.ScrollRightButton $isMobileView={isMobileView} type='button' tabIndex={-1} aria-hidden={true} onClick={scrollRight} style={{ zIndex: 10 }}>
					<IconRegularChevronRight color={'icon.inverse'} />
				</SC.ScrollRightButton>
			)}
		</SC.TabsContainer>
	)
}

const Step0ServiceSelection = (props: Props) => {
	const { onClose, onBackButtonClick, onServiceCardButtonClick, salonServicesData, selectedIndustryData, fallbackHeaderImage, phoneNumber } = props
	const { messages } = useMessages()

	const [selectedIndustry, setSelectedIndustry] = useState<SelectedIndustryData>(selectedIndustryData)
	const selectedTabKey = selectedIndustry.industryID

	const [serviceDetail, setServiceDetail] = useState<ServiceDetailData | null>(null)

	const headerImage = selectedIndustry.image || fallbackHeaderImage

	const layoutWrapperRef = useRef<HTMLDivElement>(null)

	const isServiceDetailVisible = !!serviceDetail
	// Matches SC.MobileTabsWrapper / SC.DesktopTabsWrapper visibility at BREAKPOINTS.lg
	const isMobileTabsView = useIsMobile(BREAKPOINTS_INTS.lg)

	const onTabsChange = (newIndustryData: ServicesTopCategoryData) => {
		setSelectedIndustry({ industryID: newIndustryData.id, image: newIndustryData.image.resizedImages.medium })
		const event = getModalCategoryClickedEvent({ category: newIndustryData.id })
		pushToDataLayer(event)
	}

	useEffect(() => {
		/**
		 * Disable interaction with service selection screen when service detail is visible over it
		 * The inert attribute can be added to sections of content that should not be interactive. When an element is inert, it along with all of the element's descendants, including normally interactive elements such as links, buttons, and form controls are disabled because they cannot receive focus or be clicked.
		 * The inert attribute can also be added to elements that should be offscreen or hidden. An inert element, along with its descendants, gets removed from the tab order and accessibility tree.
		 * Full docs: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inert
		 */
		if (layoutWrapperRef.current) {
			if (isServiceDetailVisible) {
				layoutWrapperRef.current.setAttribute('inert', 'true')
			} else {
				layoutWrapperRef.current.removeAttribute('inert')
			}
		}
	}, [isServiceDetailVisible])

	return (
		<>
			{serviceDetail && (
				<SalonServiceDetail
					phoneNumber={phoneNumber}
					serviceData={serviceDetail}
					onClose={() => setServiceDetail(null)}
					isReservationBookingModal
					onServiceCardButtonClick={onServiceCardButtonClick}
				/>
			)}

			<SC.LayoutWrapper ref={layoutWrapperRef} $isServiceDetailVisible={isServiceDetailVisible}>
				<ReservationBookingModalLayout
					title={messages.Services}
					headerImage={headerImage}
					onClose={onClose}
					onBackButtonClick={onBackButtonClick}
					headerContentVisibility={['mobile', null]}
					headerContent={
						<SC.MobileTabsWrapper>
							<IndustriesTabs
								isMobileView
								selectedTabKey={selectedTabKey}
								categoriesData={salonServicesData.groupedServicesByCategory}
								onTabsChange={onTabsChange}
							/>
						</SC.MobileTabsWrapper>
					}
				>
					<>
						{/* NOTE: tabs for desktop view */}
						<SC.DesktopTabsWrapper>
							<IndustriesTabs
								isMobileView={false}
								selectedTabKey={selectedTabKey}
								categoriesData={salonServicesData.groupedServicesByCategory}
								onTabsChange={onTabsChange}
							/>
						</SC.DesktopTabsWrapper>

						<SC.TabsContent>
							{salonServicesData.groupedServicesByCategory.map((industry) => {
								if (!industry.category) {
									return null
								}

								const key = industry.category.id
								const isActive = selectedTabKey === key

								// no need to render inactive tab content
								if (!isActive) {
									return null
								}

								return (
									<SC.TabContent
										key={key}
										$isActive={isActive}
										role='tabpanel'
										id={getIndustryPanelId(key)}
										aria-labelledby={getTabButtonKey(isMobileTabsView, key)}
									>
										<SalonServicesCards
											phoneNumber={phoneNumber}
											isReservationBookingModal
											industryData={industry.category}
											onServiceCardButtonClick={onServiceCardButtonClick}
											setServiceDetail={setServiceDetail}
										/>
									</SC.TabContent>
								)
							})}
						</SC.TabsContent>
					</>
				</ReservationBookingModalLayout>
			</SC.LayoutWrapper>
		</>
	)
}

export default Step0ServiceSelection
